import {
	ButtonPrimary,
} from "@telefonica/mistica";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { CollapsibleContentUp } from "..";
import { strings } from "../../localization";
import type { TotalAnticipationShelf } from "../../shelves";
import { formatAmount, formatPercentage } from "../../utils/formatters";
import { AnticipationDetails } from "../AnticipationDetails";
import { useAnalytics } from "../../hooks/useAnalytics";
import { MIN_VALUE_ANTICIPATION } from "../../constants";
import { VStack, Box } from "@chakra-ui/react";
import SelectRadius from "../SelectRadius";
import type { TotalSimulationResponse } from "../../services/APIService/types";

interface IProps {
	totalAnticipationShelf: TotalAnticipationShelf;
	offers: TotalSimulationResponse[];
	isLoading?: boolean;
	onConfirm(): void;
	onSelectOffer(selectedOffer: TotalSimulationResponse): void;
	currentComponentControl(): void;
}

export const TotalAnticipation: React.FC<IProps> = observer((props) => {
	const { totalAnticipationShelf, isLoading, onConfirm, currentComponentControl, onSelectOffer, offers } = props;
	const {
		blockedValue,
		CET,
		contractFees,
		disbursedIssueAmount,
		yearsAdvance,
		IOF,
		yearsFees,
		tacAmount,
	} = totalAnticipationShelf;

	const {
		collapsibleTitle,
	} = strings.components.anticipationSimulation;

	const { addAnalyticEvent, updatePageView } = useAnalytics();

	const handleCollapsibleAnalytic = (): void => {
		addAnalyticEvent(
			{
				component: "body",
				action: "click.button",
				description: collapsibleTitle,
				step: "fgts:simulador-quente:resultado:total",
				value: disbursedIssueAmount.value,
			},
			{ applySanitize: true },
		);
	};

	useEffect(() => {
		currentComponentControl();
	}, []);

	useEffect(() => {
		updatePageView({
			name: "Vivo Money FGTS Simulador Quente Resultado",
			pagePath: "/fgts/simulador-quente/resultado",
		});
	}, []);

	// Gera as opções de simulação com base nas ofertas simuladas
	const generateSimulations = () => offers.map((offer) => {
		const { yearsInAdvance: years, disbursedIssueAmount: simulatedValue, monthlyRate } = offer;

		return {
			id: String(years),
			antecipationYears: `Antecipar ${years} ${years === 1 ? "ano" : "anos"}`,
			valueAmount: formatAmount(simulatedValue),
			fees: `Juros ${formatPercentage(monthlyRate)} ao mês`,
			years,
		};
	});

	const options = generateSimulations();

	const [selected, setSelected] = useState(String(options.length));

	return (
		<Box>
			<VStack key="tab-total" maxWidth="400px" margin="auto" gap={6}>
				<Box
					maxHeight="670px"
					overflowY="scroll"
					width="100%"
					margin="auto"
					pr={2}
					mt={8}
					sx={{
						"::-webkit-scrollbar": { width: "8px" },
						"::-webkit-scrollbar-track": { background: "transparent" },
						"::-webkit-scrollbar-thumb": { background: "#660099", borderRadius: "4px" },
						"::-webkit-scrollbar-thumb:hover": { background: "#999" },
						"::-webkit-scrollbar-button": { display: "none", width: 0, height: 0 },
						"scrollbarWidth": "thin",
						"scrollbarColor": "#660099 transparent",
					}}
				>
					<VStack spacing={6}>
						{options.map((option, index) => (
							<SelectRadius
								key={option.id}
								antecipationYears={option.antecipationYears}
								antecipationValue={option.valueAmount}
								description={option.fees}
								isSelected={selected === option.id}
								onSelect={() => {
									setSelected(option.id);
									yearsAdvance.set(option.years);
									// ao clicar, seleciona a oferta correspondente
									onSelectOffer(offers[index]);
								}}
							/>
						))}
					</VStack>
				</Box>
			</VStack>

			<Box pb={6} pt={8}>
				<ButtonPrimary
					disabled={isLoading || disbursedIssueAmount.value < MIN_VALUE_ANTICIPATION}
					showSpinner={isLoading}
					onPress={onConfirm}
					className="button-full-width"
					dataAttributes={{ testid: "anticipationButton" }}
				>
					{strings.components.buttons.anticipateFGTS}
				</ButtonPrimary>
			</Box>

			<CollapsibleContentUp title={collapsibleTitle} analytic={handleCollapsibleAnalytic}>
				<AnticipationDetails
					blockedValue={blockedValue.value}
					contractFees={contractFees.value}
					IOF={IOF.value}
					CET={CET.value || 0}
					yearsFees={yearsFees.value || 0}
					yearsAdvance={yearsAdvance.value}
					tacAmount={tacAmount.value}
				/>
			</CollapsibleContentUp>
		</Box>
	);
});
